import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailUserSetupTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateEmail,
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { Languages } from "../../../constants/languages";
import useScrollToTop from "../../hooks/useScrollToTop";

const clearAllFieldErrors = (setters: {
  setFirstNameErr: (v: boolean) => void;
  setLastNameErr: (v: boolean) => void;
  setUserIdErr: (v: boolean) => void;
  setNewPasswordErr: (v: boolean) => void;
  setRoleNameErr: (v: boolean) => void;
  setOutletAccessErr: (v: boolean) => void;
  setQuickIdErr: (v: boolean) => void;
  setEmailErr: (v: boolean) => void;
  setCommissionClassErr: (v: boolean) => void;
  setApplicationAccessErr: (v: boolean) => void;
  setLogOffMinutesErr: (v: boolean) => void;
}) => {
  setters.setFirstNameErr(false);
  setters.setLastNameErr(false);
  setters.setUserIdErr(false);
  setters.setNewPasswordErr(false);
  setters.setRoleNameErr(false);
  setters.setOutletAccessErr(false);
  setters.setQuickIdErr(false);
  setters.setEmailErr(false);
  setters.setCommissionClassErr(false);
  setters.setApplicationAccessErr(false);
  setters.setLogOffMinutesErr(false);
};

const RetailUserSetup = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [quickId, setQuickId] = useState<string | null>(null);
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>("English");
  const [passwordExpirationDate, setPasswordExpirationDate] =
    useState<Dayjs | null>(null);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [applicationAccess, setApplicationAccess] = useState<string | null>(
    "Retail",
  );
  const [active, setActive] = useState<boolean>(true);
  const [allowGratuity, setAllowGratuity] = useState<boolean>(false);
  const [allowServiceCharge, setAllowServiceCharge] = useState<boolean>(false);
  const [allowCommission, setAllowCommission] = useState<boolean>(false);
  const [commissionClass, setCommissionClass] = useState<string | null>(null);
  const [outletAccess, setOutletAccess] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [accountBlocked, setAccountBlocked] = useState<boolean>(false);
  const [autoLogOff, setAutoLogOff] = useState<boolean>(false);
  const [logOffMinutes, setLogOffMinutes] = useState<string | null>(null);

  const [firstNameErr, setFirstNameErr] = useState<boolean>(false);
  const [lastNameErr, setLastNameErr] = useState<boolean>(false);
  const [userIdErr, setUserIdErr] = useState<boolean>(false);
  const [newPasswordErr, setNewPasswordErr] = useState<boolean>(false);
  const [roleNameErr, setRoleNameErr] = useState<boolean>(false);
  const [outletAccessErr, setOutletAccessErr] = useState<boolean>(false);
  const [quickIdErr, setQuickIdErr] = useState<boolean>(false);
  const [emailErr, setEmailErr] = useState<boolean>(false);
  const [commissionClassErr, setCommissionClassErr] = useState<boolean>(false);
  const [applicationAccessErr, setApplicationAccessErr] =
    useState<boolean>(false);
  const [logOffMinutesErr, setLogOffMinutesErr] = useState<boolean>(false);

  const [retailUsers, setRetailUsers] = useState<any>(null);
  const [outletAccessList, setOutletAccessList] = useState<any>([]);
  const [roleNameList, setRoleNameList] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "First Name": firstName,
    "Last Name": lastName,
    "User ID": userId,
    "Quick ID": quickId,
    "Email Address": emailAddress,
    Language: language,
    "Password Expiration Date": passwordExpirationDate
      ? passwordExpirationDate.toISOString()
      : "",
    "New Password": newPassword,
    "Application Access": applicationAccess,
    Active: active ? "Yes" : "No",
    "Allow Gratuity": allowGratuity ? "Yes" : "No",
    "Allow Service Charge": allowServiceCharge ? "Yes" : "No",
    "Allow Commission": allowCommission ? "Yes" : "No",
    "Commission Class": commissionClass,
    "Outlet Access": outletAccess,
    "Role Name": roleName,
    "Account Blocked": accountBlocked ? "Yes" : "No",
    "Auto Log Off": autoLogOff ? "Yes" : "No",
    "Log Off # of Minutes": logOffMinutes,
  };

  const validateInput = () => {
    clearAllFieldErrors({
      setFirstNameErr,
      setLastNameErr,
      setUserIdErr,
      setNewPasswordErr,
      setRoleNameErr,
      setOutletAccessErr,
      setQuickIdErr,
      setEmailErr,
      setCommissionClassErr,
      setApplicationAccessErr,
      setLogOffMinutesErr,
    });

    if (!firstName || String(firstName).trim() === "") {
      setFirstNameErr(true);
      return false;
    }
    if (
      !validateString(
        firstName,
        validationLength.firstName,
        validationType.SPACE,
      )
    ) {
      setFirstNameErr(true);
      return false;
    }

    if (!lastName || String(lastName).trim() === "") {
      setLastNameErr(true);
      return false;
    }
    if (
      !validateString(lastName, validationLength.lastName, validationType.SPACE)
    ) {
      setLastNameErr(true);
      return false;
    }

    if (!userId || String(userId).trim() === "") {
      setUserIdErr(true);
      return false;
    }
    if (
      !validateString(
        userId,
        validationLength.retailUserId,
        validationType.CODE,
      )
    ) {
      setUserIdErr(true);
      return false;
    }

    if (!newPassword || String(newPassword).trim() === "") {
      setNewPasswordErr(true);
      return false;
    }
    if (
      !validateString(
        newPassword,
        validationLength.retailUserSetupPassword,
        validationType.NAME,
      )
    ) {
      setNewPasswordErr(true);
      return false;
    }

    if (!roleName || String(roleName).trim() === "") {
      setRoleNameErr(true);
      return false;
    }
    if (
      !validateString(
        roleName,
        validationLength.retailRoleName,
        validationType.NAME,
      )
    ) {
      setRoleNameErr(true);
      return false;
    }

    if (!outletAccess || String(outletAccess).trim() === "") {
      setOutletAccessErr(true);
      return false;
    }
    if (
      !validateString(
        outletAccess,
        validationLength.outletName,
        validationType.NAME,
      )
    ) {
      setOutletAccessErr(true);
      return false;
    }

    if (
      quickId &&
      (quickId.length > validationLength.retailUserSetupQuickIdDigits ||
        !/^\d+$/.test(quickId))
    ) {
      setQuickIdErr(true);
      return false;
    }

    const em = emailAddress?.trim();
    if (em && !validateEmail(em)) {
      setEmailErr(true);
      return false;
    }

    if (
      commissionClass &&
      String(commissionClass).trim() !== "" &&
      !validateString(
        commissionClass,
        validationLength.outletName,
        validationType.NAME,
      )
    ) {
      setCommissionClassErr(true);
      return false;
    }

    if (
      applicationAccess &&
      String(applicationAccess).trim() !== "" &&
      !validateString(
        applicationAccess,
        validationLength.outletName,
        validationType.SPACE,
      )
    ) {
      setApplicationAccessErr(true);
      return false;
    }

    if (logOffMinutes && String(logOffMinutes).trim() !== "") {
      if (!/^\d+$/.test(logOffMinutes) || logOffMinutes.length > 4) {
        setLogOffMinutesErr(true);
        return false;
      }
    }

    return true;
  };

  const editUser = (list: any) => {
    clearAllFieldErrors({
      setFirstNameErr,
      setLastNameErr,
      setUserIdErr,
      setNewPasswordErr,
      setRoleNameErr,
      setOutletAccessErr,
      setQuickIdErr,
      setEmailErr,
      setCommissionClassErr,
      setApplicationAccessErr,
      setLogOffMinutesErr,
    });
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setFirstName(null);
      setLastName(null);
      setUserId(null);
      setQuickId(null);
      setEmailAddress(null);
      setLanguage("English");
      setPasswordExpirationDate(null);
      setNewPassword(null);
      setApplicationAccess("Retail");
      setActive(true);
      setAllowGratuity(false);
      setAllowServiceCharge(false);
      setAllowCommission(false);
      setCommissionClass(null);
      setOutletAccess(null);
      setRoleName(null);
      setAccountBlocked(false);
      setAutoLogOff(false);
      setLogOffMinutes(null);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setFirstName(list?.["First Name"] ?? null);
    setLastName(list?.["Last Name"] ?? null);
    setUserId(list?.["User ID"] ?? null);
    setQuickId(list?.["Quick ID"] ?? null);
    setEmailAddress(list?.["Email Address"] ?? null);
    setLanguage(list?.Language ?? "English");
    setPasswordExpirationDate(
      list?.["Password Expiration Date"]
        ? dayjs(list["Password Expiration Date"])
        : null,
    );
    setNewPassword(list?.["New Password"] ?? null);
    setApplicationAccess(list?.["Application Access"] ?? "Retail");
    setActive(list?.["Active"] === "Yes");
    setAllowGratuity(list?.["Allow Gratuity"] === "Yes");
    setAllowServiceCharge(list?.["Allow Service Charge"] === "Yes");
    setAllowCommission(list?.["Allow Commission"] === "Yes");
    setCommissionClass(list?.["Commission Class"] ?? null);
    setOutletAccess(list?.["Outlet Access"] ?? null);
    setRoleName(list?.["Role Name"] ?? null);
    setAccountBlocked(list?.["Account Blocked"] === "Yes");
    setAutoLogOff(list?.["Auto Log Off"] === "Yes");
    setLogOffMinutes(
      list?.["Log Off # of Minutes"] != null
        ? String(list["Log Off # of Minutes"])
        : null,
    );
  };

  const updateUser = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        (retailUsers ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.USERID,
        userId,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailUsers((prev: any) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editUser(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RetailPropertyEnum.userSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addUser = () => {
    if (!validateInput()) return;
    if (checkDuplicate(retailUsers ?? [], DNDFieldsEnum.USERID, userId)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailUsers((prev: any) => {
      const next =
        prev !== null && prev.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }];
      return updateData(next);
    });
    setLastUpdatedRecord(payload);
    editUser(null);
    setEditClicked(false);
  };

  const deleteUser = (id: number) => {
    setRetailUsers(deleteItem(retailUsers, id));
  };

  useEffect(() => {
    if (fieldValues) {
      setRetailUsers(fieldValues[RetailPropertyEnum.userSetup]);
      setOutletAccessList(
        fillAutocompleteValues(
          fieldValues[RetailPropertyEnum.outletSetup],
          DNDFieldsEnum.OUTLETNAME,
          "",
        ),
      );
      setRoleNameList(
        fillAutocompleteValues(
          fieldValues[RetailPropertyEnum.roleSetup],
          DNDFieldsEnum.ROLENAME,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(retailUsers);
    editClicked &&
      setSelectedId(checkData(retailUsers, selectedId) ? selectedId : null);
  }, [retailUsers]);

  const firstNameHelperText = firstNameErr
    ? !firstName || String(firstName).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailUserSetupFirstName
    : fieldValidationHelperText.retailUserSetupFirstName;

  const lastNameHelperText = lastNameErr
    ? !lastName || String(lastName).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailUserSetupLastName
    : fieldValidationHelperText.retailUserSetupLastName;

  const userIdHelperText = userIdErr
    ? !userId || String(userId).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailUserSetupUserId
    : fieldValidationHelperText.retailUserSetupUserId;

  const newPasswordHelperText = newPasswordErr
    ? !newPassword || String(newPassword).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailUserSetupNewPassword
    : fieldValidationHelperText.retailUserSetupNewPassword;

  const quickIdHelperText = quickIdErr
    ? validationMessages.retailUserSetupQuickId
    : fieldValidationHelperText.retailUserSetupQuickId;

  const emailHelperText = emailErr
    ? validationMessages.email
    : fieldValidationHelperText.retailUserSetupEmail;

  const commissionClassHelperText = commissionClassErr
    ? validationMessages.retailUserSetupCommissionClass
    : fieldValidationHelperText.retailUserSetupCommissionClass;

  const applicationAccessHelperText = applicationAccessErr
    ? validationMessages.retailUserSetupApplicationAccess
    : fieldValidationHelperText.retailUserSetupApplicationAccess;

  const logOffMinutesHelperText = logOffMinutesErr
    ? validationMessages.retailUserSetupLogOffMinutes
    : fieldValidationHelperText.retailUserSetupLogOffMinutes;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              User Setup
              <Tooltip title={tooltipComponent(retailUserSetupTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editUser(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editUser(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="column" width="100%" gap="20px">
            <Stack>
              <FormLabel component="legend">
                First Name
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.firstName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailUserFirstName"
                variant="outlined"
                placeholder="First Name"
                helperText={firstNameHelperText}
                error={firstNameErr}
                value={firstName ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFirstName(e.target.value || null);
                  setFirstNameErr(false);
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Last Name
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.lastName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailUserLastName"
                variant="outlined"
                placeholder="Last Name"
                helperText={lastNameHelperText}
                error={lastNameErr}
                value={lastName ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setLastName(e.target.value || null);
                  setLastNameErr(false);
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                User ID
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.userId)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailUserId"
                variant="outlined"
                placeholder="User ID"
                helperText={userIdHelperText}
                error={userIdErr}
                value={userId ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserId(e.target.value || null);
                  setUserIdErr(false);
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Quick ID
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.quickId)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailQuickId"
                variant="outlined"
                placeholder="Quick ID"
                helperText={quickIdHelperText}
                error={quickIdErr}
                value={quickId ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setQuickId(v || null);
                  setQuickIdErr(false);
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Email Address
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.emailAddress)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailEmailAddress"
                variant="outlined"
                placeholder="Email Address"
                helperText={emailHelperText}
                error={emailErr}
                value={emailAddress ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmailAddress(e.target.value || null);
                  setEmailErr(false);
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Language
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.language)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                disablePortal
                options={Languages.filter((l) => l === "English")}
                sx={{ width: "100%" }}
                value={language ?? "English"}
                onChange={(_e: any, v: string | null) =>
                  setLanguage(v ?? "English")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Language"
                    helperText={
                      fieldValidationHelperText.retailUserSetupLanguage
                    }
                  />
                )}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Password Expiration Date
                <Tooltip
                  title={tooltipComponent(
                    retailUserSetupTooltip.passwordExpirationDate,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={passwordExpirationDate}
                  onChange={(v) => setPasswordExpirationDate(v)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "Password Expiration Date",
                      helperText:
                        fieldValidationHelperText.retailUserSetupPasswordExpiration,
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>
            <Stack>
              <FormLabel component="legend">
                New Password
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.newPassword)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailNewPassword"
                variant="outlined"
                type="password"
                placeholder="New Password"
                helperText={newPasswordHelperText}
                error={newPasswordErr}
                value={newPassword ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setNewPassword(e.target.value || null);
                  setNewPasswordErr(false);
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Application Access
                <Tooltip
                  title={tooltipComponent(
                    retailUserSetupTooltip.applicationAccess,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailApplicationAccess"
                variant="outlined"
                placeholder="Application Access"
                helperText={applicationAccessHelperText}
                error={applicationAccessErr}
                value={applicationAccess ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setApplicationAccess(e.target.value || null);
                  setApplicationAccessErr(false);
                }}
              />
            </Stack>

            <Stack>
              <FormLabel component="legend">
                Active?
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.active)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label=""
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Allow Gratuity?
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.allowGratuity)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowGratuity}
                    onChange={() => setAllowGratuity(!allowGratuity)}
                  />
                }
                label=""
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Allow Service Charge?
                <Tooltip
                  title={tooltipComponent(
                    retailUserSetupTooltip.allowServiceCharge,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowServiceCharge}
                    onChange={() => setAllowServiceCharge(!allowServiceCharge)}
                  />
                }
                label=""
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Allow Commission?
                <Tooltip
                  title={tooltipComponent(
                    retailUserSetupTooltip.allowCommission,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowCommission}
                    onChange={() => setAllowCommission(!allowCommission)}
                  />
                }
                label=""
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Commission Class
                <Tooltip
                  title={tooltipComponent(
                    retailUserSetupTooltip.commissionClass,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailCommissionClass"
                variant="outlined"
                placeholder="Commission Class"
                helperText={commissionClassHelperText}
                error={commissionClassErr}
                value={commissionClass ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCommissionClass(e.target.value || null);
                  setCommissionClassErr(false);
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Outlet Access
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.outletAccess)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Stack direction="row" width="100%" gap="5px">
                <Autocomplete
                  id="retailOutletAccess"
                  disablePortal
                  options={removeSampleTestArray(outletAccessList ?? [])}
                  sx={{ width: "100%" }}
                  value={outletAccess}
                  disabled={!outletAccessList}
                  onChange={(_e, newValue) => {
                    setOutletAccess(newValue ?? null);
                    setOutletAccessErr(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Outlet Access"
                      variant="outlined"
                      placeholder="Outlet Access"
                      error={outletAccessErr}
                      helperText={
                        outletAccessErr
                          ? !outletAccess || String(outletAccess).trim() === ""
                            ? validationMessages.fieldRequired
                            : validationMessages.retailUserSetupOutletAccess
                          : ""
                      }
                    />
                  )}
                />
              </Stack>
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Role Name
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.roleNameUser)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Stack direction="row" width="100%" gap="5px">
                <Autocomplete
                  id="retailRoleName"
                  disablePortal
                  options={removeSampleTestArray(roleNameList ?? [])}
                  sx={{ width: "100%" }}
                  value={roleName}
                  disabled={!roleNameList}
                  onChange={(_e, newValue) => {
                    setRoleName(newValue ?? null);
                    setRoleNameErr(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Role Name"
                      variant="outlined"
                      placeholder="Role Name"
                      error={roleNameErr}
                      helperText={
                        roleNameErr
                          ? !roleName || String(roleName).trim() === ""
                            ? validationMessages.fieldRequired
                            : validationMessages.retailRoleSetupName
                          : ""
                      }
                    />
                  )}
                />
              </Stack>
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Account Blocked?
                <Tooltip
                  title={tooltipComponent(
                    retailUserSetupTooltip.accountBlocked,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={accountBlocked}
                    onChange={() => setAccountBlocked(!accountBlocked)}
                  />
                }
                label=""
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Auto Log Off?
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.autoLogOff)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoLogOff}
                    onChange={() => setAutoLogOff(!autoLogOff)}
                  />
                }
                label=""
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                Log Off # of Minutes
                <Tooltip
                  title={tooltipComponent(retailUserSetupTooltip.logOffMinutes)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="retailLogOffMinutes"
                variant="outlined"
                placeholder="Log Off # of Minutes"
                helperText={logOffMinutesHelperText}
                error={logOffMinutesErr}
                value={logOffMinutes ?? ""}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setLogOffMinutes(v || null);
                  setLogOffMinutesErr(false);
                }}
              />
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateUser : addUser}
              id="addRetailUser"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} User
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">User Setup Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailUsers ? (
                <DraggableItemList
                  itemData={retailUsers}
                  onOrderUpdate={(order) => setRetailUsers(order)}
                  onEdit={(info) => {
                    editUser(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteUser(id)}
                  code={DNDFieldsEnum.USERID}
                  name={DNDFieldsEnum.FIRSTNAME}
                />
              ) : (
                <Typography variant="body2">
                  No User Setup Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailUserSetup;
